import { useEffect, useRef, useState } from "react";
import { MESSAGE_POLL_DURATIONS, type CreateMessagePoll } from "@atrium/shared";
import { Dialog } from "../../components/ui/Dialog";
import { Button, IconButton } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Settings";
import { Switch } from "../../components/ui/Controls";
import { IconPlus, IconSmile, IconX } from "../../components/icons";
import { EmojiPicker } from "./EmojiPicker";

const MAX_ANSWERS = 10;

function durationLabel(h: number) {
  if (h < 24) return `${h} ${h === 1 ? "hour" : "hours"}`;
  const d = h / 24;
  if (d % 7 === 0) return `${d / 7} ${d === 7 ? "week" : "weeks"}`;
  return `${d} ${d === 1 ? "day" : "days"}`;
}
const DURATIONS = MESSAGE_POLL_DURATIONS.map((h) => ({ value: String(h), label: durationLabel(h) }));

interface Answer {
  key: number;
  text: string;
  emoji: string | null;
}

let seq = 0;
const blank = (): Answer => ({ key: ++seq, text: "", emoji: null });

/** Build a poll for a channel message: question, 2–10 answers (optional emoji), several choices, length. */
export function PollComposer({ open, onOpenChange, onSubmit }: { open: boolean; onOpenChange: (o: boolean) => void; onSubmit: (poll: CreateMessagePoll) => void }) {
  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState<Answer[]>(() => [blank(), blank()]);
  const [multiple, setMultiple] = useState(false);
  const [hours, setHours] = useState(24);
  const [tried, setTried] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuestion("");
    setAnswers([blank(), blank()]);
    setMultiple(false);
    setHours(24);
    setTried(false);
  }, [open]);

  const filled = answers.filter((a) => a.text.trim());
  const dupes = new Set(filled.map((a) => a.text.trim().toLowerCase())).size !== filled.length;
  const error = !question.trim() ? "Ask a question" : filled.length < 2 ? "Add at least two answers" : dupes ? "Each answer must be different" : null;

  const set = (key: number, patch: Partial<Answer>) => setAnswers((list) => list.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  const add = () => {
    if (answers.length >= MAX_ANSWERS) return;
    setAnswers((l) => [...l, blank()]);
    requestAnimationFrame(() => listRef.current?.querySelector<HTMLInputElement>("div:last-child input")?.focus());
  };

  const submit = () => {
    setTried(true);
    if (error) return;
    onSubmit({
      question: question.trim(),
      options: filled.map((a) => ({ text: a.text.trim(), ...(a.emoji ? { emoji: a.emoji } : {}) })),
      multiple,
      durationHours: hours,
    });
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create a poll"
      footer={
        <>
          {tried && error && (
            <span className="mr-auto text-sm text-danger" role="alert">
              {error}
            </span>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit}>
            Post
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={300} placeholder="Question" aria-label="Question" aria-invalid={tried && !question.trim()} />
        <div ref={listRef} className="space-y-2" role="group" aria-label="Answers">
          {answers.map((a, i) => (
            <div key={a.key} className="flex items-center gap-1.5">
              <EmojiPicker onPick={(e) => set(a.key, { emoji: a.emoji === e ? null : e })} align="start" label="Emoji">
                <button
                  type="button"
                  aria-label={a.emoji ? `Answer ${i + 1} emoji: ${a.emoji}. Change` : `Add an emoji to answer ${i + 1}`}
                  className="flex size-10 shrink-0 items-center justify-center rounded-md border border-line-strong bg-surface text-xl text-fg-3 hover:text-fg"
                >
                  {a.emoji ?? <IconSmile className="icon size-5" aria-hidden />}
                </button>
              </EmojiPicker>
              <Input
                value={a.text}
                maxLength={55}
                onChange={(e) => set(a.key, { text: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (i === answers.length - 1 && answers.length < MAX_ANSWERS) add();
                    else listRef.current?.querySelectorAll<HTMLInputElement>("input")[i + 1]?.focus();
                  }
                }}
                placeholder={`Answer ${i + 1}`}
                aria-label={`Answer ${i + 1}`}
              />
              {answers.length > 2 && <IconButton size="sm" label={`Remove answer ${i + 1}`} icon={IconX} onClick={() => setAnswers((l) => l.filter((x) => x.key !== a.key))} />}
            </div>
          ))}
          {answers.length < MAX_ANSWERS && (
            <Button size="sm" variant="ghost" icon={IconPlus} onClick={add}>
              Add answer
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
          <label className="flex items-center gap-2.5 text-sm text-fg-2">
            <Switch label="Allow several answers" checked={multiple} onCheckedChange={setMultiple} />
            Allow several answers
          </label>
          <Select aria-label="Poll length" value={String(hours)} onChange={(v) => setHours(Number(v))} options={DURATIONS} />
        </div>
      </div>
    </Dialog>
  );
}
